import styled from "styled-components";
import { spacing } from "src/design-system/tokens/spacing";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { useIntl } from "react-intl";
import { tw } from "src/design-system/tw";
import { screenMinWidth } from "src/theme";
import { getFormattedDurationText } from "../../../components/FormattedDuration/FormattedDuration";
import {
  type FormattedPriceRangeProps,
  formatPriceRange,
} from "../../../components/FormattedPriceRange/FormattedPriceRange";
import { PaneNav } from "../../PaneNav/PaneNav";

type RouteHeaderProps = {
  title: string;
  durationInMinutes: number;
  priceRange?: FormattedPriceRangeProps;
  className?: string;
  routeCanonical: string;
  onBackClick: () => void;
  hidePaneNav?: boolean;
};

export function RouteHeader(props: RouteHeaderProps) {
  const { isMobile } = useScreenMinWidth();
  const showPaneNav = !isMobile && !props.hidePaneNav;
  const intl = useIntl();

  const formattedDuration = getFormattedDurationText(
    props.durationInMinutes,
    intl,
  );

  const formattedPriceRange = props.priceRange
    ? formatPriceRange(
        intl,
        props.priceRange.low,
        props.priceRange.high,
        props.priceRange.currencyCode,
        props.priceRange.showDecimals,
      )
    : "";

  const informationLabelText = formattedPriceRange
    ? `${formattedDuration} • ${formattedPriceRange}`
    : formattedDuration;

  return (
    <>
      {showPaneNav && <PaneNav onBackClick={props.onBackClick} />}
      <HeaderWrapper className={props.className}>
        <TitleInfoContainer>
          <h4
            className={tw(
              "font-medium",
              isMobile ? "text-heading-sm" : "text-heading-lg",
            )}
          >
            {props.title}
          </h4>
          <Information>
            <span className="text-label-lg text-text-primary">
              {informationLabelText}
            </span>
          </Information>
        </TitleInfoContainer>
      </HeaderWrapper>
    </>
  );
}

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxl};
  padding: ${spacing.xl} ${spacing.xl} 0;

  ${screenMinWidth.sm} {
    padding: 0;
  }
`;

const TitleInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const Information = styled.div`
  display: flex;
  gap: ${spacing.md};
  align-items: center;
`;
